import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { ThemeToggle } from '../common/ThemeToggle';
import { PageLoading } from '../common/LoadingSpinner';
import { NotificationsBell } from '../common/NotificationsBell';
import GlobalQuickJump from '../common/GlobalQuickJump';
import { QRScannerButton } from '../common/QRScannerButton';
import { Button } from '../ui/button';
import { AppFooter } from './AppFooter';
import { NoAccess } from './NoAccess';
import ChangePasswordDialog from '../common/ChangePasswordDialog';
import ErrorBoundary from '../common/ErrorBoundary';
import { NAV_MODULES, GROUP_LABELS } from './navConfig';
import { searchNav, matchNavEntry, canSeeEntry, groupModules } from './navSearch';
import {
  SignOut,
  CaretLeft,
  CaretDown,
  MagnifyingGlass,
  XCircle,
  ArrowRight,
} from '@phosphor-icons/react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import api from '../../api/client';

// The 26 modules and their sub-pages live in navConfig.js; the matching and
// search rules in navSearch.js.
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

const JSSW_LOGO = "https://customer-assets.emergentagent.com/job_jina-users/artifacts/bk4divlg_Company%20Logo.png";

export function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(JSSW_LOGO);
  const { user, logout, checkAuth } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  // An account on an issued password gets a banner, not a modal: a modal that
  // opened by itself covered every screen and, on staging, would not close.
  // "Later" hides the banner for this sign-in only (keyed on the session
  // token), so the next sign-in shows it again.
  const pwTokenKey = () => { try { return localStorage.getItem('access_token') || ''; } catch { return ''; } };
  const [pwDeferred, setPwDeferred] = useState(() => {
    try { return !!pwTokenKey() && localStorage.getItem('pw_change_deferred_for') === pwTokenKey(); } catch { return false; }
  });
  const deferPwChange = () => {
    setPwDeferred(true);
    try { localStorage.setItem('pw_change_deferred_for', pwTokenKey()); } catch { /* banner just hides */ }
  };
  const { hasModuleAccess, permissionsLoaded, isSuperAdmin } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await api.get('/settings/logo');
        if (response.data?.success && response.data?.data?.logo_url) {
          setCompanyLogo(response.data.data.logo_url);
        }
      } catch (err) {
        // Use default
      }
    };
    fetchLogo();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Who may see what.  canSee never hides the whole menu when the session has
  // no permission list (see navSearch.canSeeEntry).
  const visibility = { isSuperAdmin, permissionsLoaded, hasModuleAccess };
  const canSee = (entry) => canSeeEntry(entry, visibility);
  const modules = NAV_MODULES.filter(canSee);
  const groups = groupModules(modules, GROUP_LABELS);

  // Where we are: the module that owns the URL, and its sub-page if any.
  const current = matchNavEntry(NAV_MODULES, location.pathname);
  const activeModuleKey = current?.module?.key;
  const accessOk = !current || (canSee(current.module) && (!current.child || canSee(current.child)));

  // Search results, filtered to what this role may open.
  // Filter by permission BEFORE cutting to 12, or a restricted role sees a
  // shorter (or empty) list than the query deserves.
  const results = query.trim() ? searchNav(NAV_MODULES, query, { limit: 1000 }).filter((r) => canSee(r.entry)).slice(0, 12) : [];

  useEffect(() => { setActiveIdx(0); }, [query]);
  useEffect(() => { setQuery(''); }, [location.pathname]);

  // Ctrl/Cmd+K focuses the module search ("/" already belongs to the record
  // quick-jump in the header).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        setSidebarCollapsed(false);
        window.requestAnimationFrame(() => searchRef.current?.focus());
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const go = (path) => {
    setQuery('');
    navigate(path);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (results[activeIdx]) go(results[activeIdx].entry.path);
    } else if (e.key === 'Escape') {
      setQuery('');
      e.currentTarget.blur();
    }
  };

  const openSearch = () => {
    setSidebarCollapsed(false);
    window.requestAnimationFrame(() => searchRef.current?.focus());
  };

  const navTestId = (label) => `nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;

  return (
    // `--app-sidebar-w` publishes the live sidebar width to the whole subtree so
    // that any viewport-fixed element inside a page (e.g. the report action bar)
    // can start where the sidebar ends instead of sitting on top of it.
    <div
      className="flex min-h-screen relative"
      data-sidebar={sidebarCollapsed ? 'collapsed' : 'expanded'}
      style={{
        backgroundColor: 'var(--bg-primary)',
        '--app-sidebar-w': sidebarCollapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)',
      }}
    >
      {/* Sidebar — always dark premium */}
      <aside
        className="fixed left-0 top-0 h-full z-50 flex flex-col sidebar-always-dark"
        style={{
          width: sidebarCollapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)',
          transition: 'width 300ms cubic-bezier(0.22, 1, 0.36, 1)',
          borderRight: '1px solid rgba(255,255,255,0.05)',
          boxShadow: '0 0 30px rgba(0,0,0,0.25)',
        }}
      >
        {/* Brand */}
        <div
          className="flex items-center justify-between px-3.5"
          style={{
            height: 'var(--header-height)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          {!sidebarCollapsed ? (
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <img src={companyLogo} alt="JSSW" className="h-6 w-auto" data-testid="sidebar-company-logo" />
              </div>
              <div className="flex flex-col leading-tight min-w-0">
                <span
                  className="text-[13px] font-semibold truncate"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    color: '#EDF1F7',
                    letterSpacing: '-0.01em',
                  }}
                >
                  JINA STEEL
                </span>
                <span
                  className="text-[10px] uppercase tracking-[0.18em] truncate"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  Management
                </span>
              </div>
            </div>
          ) : (
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center mx-auto"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <img src={companyLogo} alt="JSSW" className="h-6 w-auto" />
            </div>
          )}

          {!sidebarCollapsed && (
            <button
              onClick={() => { setSidebarCollapsed(true); setQuery(''); }}
              className="h-7 w-7 rounded-md flex items-center justify-center transition-colors"
              style={{ color: 'rgba(237,241,247,0.55)' }}
              data-testid="sidebar-toggle"
              title="Collapse"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
                e.currentTarget.style.color = 'var(--accent-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'rgba(237,241,247,0.55)';
              }}
            >
              <CaretLeft className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Expand toggle when collapsed */}
        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="mx-auto mt-2 h-7 w-7 rounded-md flex items-center justify-center transition-colors"
            style={{ color: 'rgba(237,241,247,0.55)' }}
            data-testid="sidebar-toggle-expand"
            title="Expand"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
              e.currentTarget.style.color = 'var(--accent-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'rgba(237,241,247,0.55)';
            }}
          >
            <CaretLeft className="h-4 w-4 rotate-180" />
          </button>
        )}

        {/* Module search */}
        {!sidebarCollapsed ? (
          <div className="px-3 mt-4 mb-1">
            <div
              className="flex items-center rounded-lg transition-colors"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: `1px solid ${searchFocused ? 'var(--accent-primary)' : 'rgba(255,255,255,0.08)'}`,
              }}
            >
              <MagnifyingGlass className="h-4 w-4 ml-2.5 flex-shrink-0" style={{ color: 'rgba(237,241,247,0.45)' }} />
              <input
                ref={searchRef}
                id="nav-search"
                type="text"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={Boolean(query.trim())}
                aria-controls="nav-search-results"
                aria-activedescendant={query.trim() && results[activeIdx] ? `nav-result-${activeIdx}` : undefined}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Search modules"
                aria-label="Search modules"
                autoComplete="off"
                spellCheck={false}
                className="nav-search-input flex-1 min-w-0 bg-transparent outline-none px-2 py-2 text-[13px]"
                style={{ color: '#EDF1F7', fontFamily: 'var(--font-body)' }}
                data-testid="nav-search"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => { setQuery(''); searchRef.current?.focus(); }}
                  className="mr-2 flex items-center justify-center rounded"
                  style={{ color: 'rgba(237,241,247,0.55)' }}
                  aria-label="Clear search"
                  data-testid="nav-search-clear"
                >
                  <XCircle className="h-4 w-4" weight="fill" />
                </button>
              ) : (
                <kbd
                  className="mr-2 px-1.5 py-0.5 rounded text-[10px] tracking-wide"
                  style={{
                    fontFamily: 'var(--font-body)',
                    color: 'rgba(237,241,247,0.4)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {IS_MAC ? '\u2318K' : 'Ctrl K'}
                </kbd>
              )}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={openSearch}
            className="mx-auto mt-3 mb-1 h-9 w-9 rounded-lg flex items-center justify-center transition-colors"
            style={{
              color: 'rgba(237,241,247,0.6)',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            title="Search modules (Ctrl K)"
            aria-label="Search modules"
            data-testid="nav-search-open"
          >
            <MagnifyingGlass className="h-4 w-4" />
          </button>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 pb-4 mt-1" aria-label="Modules">
          {query.trim() && !sidebarCollapsed ? (
            results.length ? (
              <ul className="space-y-0.5" role="listbox" id="nav-search-results" aria-label="Matching modules" data-testid="nav-search-results">
                {results.map((r, i) => {
                  const e = r.entry;
                  const Icon = (e.parent || e).icon;
                  const active = i === activeIdx;
                  return (
                    <li key={e.path} id={`nav-result-${i}`} role="option" aria-selected={active}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => go(e.path)}
                        className={`nav-row w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${active ? 'nav-item-active' : ''}`}
                        style={{
                          fontFamily: 'var(--font-body)',
                          color: active ? 'var(--accent-primary)' : 'rgba(237,241,247,0.72)',
                        }}
                        tabIndex={-1}
                      >
                        <Icon className="h-[18px] w-[18px] flex-shrink-0" weight={active ? 'fill' : 'duotone'} />
                        <span className="min-w-0 flex-1 flex flex-col leading-tight">
                          <span className="truncate text-[13.5px]" style={{ fontWeight: e.kind === 'module' ? 600 : 500 }}>
                            {e.label}
                          </span>
                          {e.parent && (
                            <span className="truncate text-[11px]" style={{ color: 'rgba(237,241,247,0.45)' }}>
                              {e.parent.label}
                            </span>
                          )}
                        </span>
                        {active && <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="px-3 py-3 text-[12.5px] leading-relaxed" style={{ color: 'rgba(237,241,247,0.5)' }} data-testid="nav-search-empty">
                No module matches "{query.trim()}". Try a document name such as GRN, PRC, CIOA or DC.
              </p>
            )
          ) : (
            groups.map((g, gi) => (
              <div key={g.group} className={gi === 0 ? '' : 'mt-2'}>
                {gi > 0 && (
                  sidebarCollapsed ? (
                    <div className="mx-3 my-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />
                  ) : (
                    g.label && (
                      <div className="px-3 pt-2 pb-1">
                        <span
                          className="text-[10px] uppercase tracking-[0.18em]"
                          style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'rgba(237,241,247,0.32)' }}
                        >
                          {g.label}
                        </span>
                      </div>
                    )
                  )
                )}
                <ul className="space-y-0.5">
                  {g.items.map((item) => {
                    const isActive = item.key === activeModuleKey;
                    const children = isActive && !sidebarCollapsed ? (item.children || []).filter(canSee) : [];
                    return (
                      <li key={item.key}>
                        <Link
                          to={item.path}
                          title={sidebarCollapsed ? item.label : undefined}
                          aria-current={isActive ? 'page' : undefined}
                          className={`nav-row relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${isActive ? 'nav-item-active' : ''}`}
                          style={{
                            fontFamily: 'var(--font-body)',
                            fontWeight: isActive ? 600 : 500,
                            color: isActive ? 'var(--accent-primary)' : 'rgba(237,241,247,0.68)',
                            fontSize: '13.5px',
                          }}
                          data-testid={navTestId(item.label)}
                        >
                          <item.icon
                            className="h-[18px] w-[18px] flex-shrink-0 transition-transform group-hover:scale-110"
                            weight={isActive ? 'fill' : 'duotone'}
                          />
                          {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                          {!sidebarCollapsed && isActive && (
                            <span
                              className="ml-auto w-1.5 h-1.5 rounded-full"
                              style={{ background: 'var(--accent-primary)', boxShadow: '0 0 8px var(--accent-primary-ring)' }}
                            />
                          )}
                        </Link>
                        {children.length > 0 && (
                          <ul className="mt-0.5 mb-1 ml-[21px] pl-3 space-y-px" style={{ borderLeft: '1px solid rgba(255,255,255,0.08)' }} data-testid={`${navTestId(item.label)}-children`}>
                            {children.map((c) => {
                              const childActive = current?.child?.path === c.path;
                              return (
                                <li key={c.path}>
                                  <Link
                                    to={c.path}
                                    aria-current={childActive ? 'page' : undefined}
                                    className={`nav-sub-item block truncate px-2.5 py-1.5 rounded-md text-[12.5px] transition-colors ${childActive ? 'nav-sub-item-active' : ''}`}
                                    style={{ fontFamily: 'var(--font-body)', fontWeight: childActive ? 600 : 500 }}
                                    data-testid={navTestId(c.label)}
                                  >
                                    {c.label}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </nav>

        {/* User Section */}
        <div
          className="p-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center gap-2.5 w-full p-2 rounded-lg transition-all ${
                  sidebarCollapsed ? 'justify-center' : ''
                }`}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
                data-testid="user-menu-trigger"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(212,168,87,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(212,168,87,0.22)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                }}
              >
                <Avatar className="h-8 w-8 flex-shrink-0" style={{ background: 'var(--gradient-accent)' }}>
                  <AvatarFallback
                    className="text-[12px]"
                    style={{
                      background: 'var(--gradient-accent)',
                      color: '#0A0E14',
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 700,
                    }}
                  >
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                {!sidebarCollapsed && (
                  <>
                    <div className="flex-1 text-left min-w-0">
                      <p
                        className="text-[13px] truncate"
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontWeight: 600,
                          color: '#EDF1F7',
                        }}
                      >
                        {user?.full_name || 'User'}
                      </p>
                      <p
                        className="text-[11px] truncate"
                        style={{
                          fontFamily: 'var(--font-body)',
                          color: 'rgba(237,241,247,0.5)',
                        }}
                      >
                        {user?.role?.name || 'Member'}
                      </p>
                    </div>
                    <CaretDown className="h-3 w-3" style={{ color: 'rgba(237,241,247,0.5)' }} />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="w-60"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                borderColor: 'var(--border-default)',
                boxShadow: 'var(--shadow-elevated)',
              }}
            >
              <div className="px-3 py-2.5">
                <p className="text-[13px]" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-body)', fontWeight: 600 }}>
                  {user?.full_name}
                </p>
                <p className="text-[12px] mt-0.5" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
                  {user?.email}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => setPwOpen(true)} data-testid="change-password-btn">
                Change password
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                style={{ color: 'var(--color-danger)' }}
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <SignOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content Area */}
      <div
        className="flex-1 flex flex-col min-w-0"
        style={{
          marginLeft: sidebarCollapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)',
          transition: 'margin-left 300ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Top Header */}
        <header
          className="flex items-center justify-between px-6 sticky top-0 z-40 glass"
          style={{
            height: 'var(--header-height)',
            borderBottom: '1px solid var(--border-default)',
          }}
        >
          <div className="flex items-center gap-3">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2">
              <span
                className="text-[11px] uppercase tracking-[0.15em]"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                }}
              >
                JSSW
              </span>
              <span style={{ color: 'var(--text-muted)', opacity: 0.5 }}>/</span>
              <span
                className="text-[14px]"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.01em',
                }}
                data-testid="breadcrumb-module"
              >
                {current?.module?.label || 'Workspace'}
              </span>
              {current?.child && (
                <>
                  <span style={{ color: 'var(--text-muted)', opacity: 0.5 }}>/</span>
                  <span
                    className="text-[13px] truncate max-w-[40vw]"
                    style={{ fontFamily: 'var(--font-heading)', fontWeight: 500, color: 'var(--text-secondary)' }}
                    data-testid="breadcrumb-page"
                  >
                    {current.child.label}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* P12.1 — Global PRC / SC / Charge# Quick-Jump */}
            <GlobalQuickJump />

            {/* QR Scanner */}
            <QRScannerButton />

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Notifications */}
            <NotificationsBell />

            {/* Divider */}
            <div className="h-7 w-px mx-1" style={{ background: 'var(--border-default)' }} />

            {/* User compact */}
            <div className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-full" style={{ background: 'var(--accent-primary-soft)' }}>
              <Avatar className="h-7 w-7" style={{ background: 'var(--gradient-accent)' }}>
                <AvatarFallback
                  className="text-[11px]"
                  style={{
                    background: 'var(--gradient-accent)',
                    color: '#0A0E14',
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                  }}
                >
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col leading-tight">
                <span
                  className="text-[12px]"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}
                >
                  {user?.full_name?.split(' ')[0] || 'User'}
                </span>
                <span
                  className="text-[10px] uppercase tracking-wider"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 600,
                    color: 'var(--accent-primary)',
                  }}
                >
                  {user?.role?.name || 'Member'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        {/* Page Content.  Screens are lazy chunks (see App.js), so the wait for
            one is absorbed here: the sidebar, header and footer stay on screen
            and only this area shows the spinner. */}
        <main className="flex-1 page-enter relative" style={{ backgroundColor: 'var(--bg-primary)' }}>
          {user?.must_change_password && !pwDeferred && (
            <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2 text-sm"
                 style={{ backgroundColor: 'rgba(245, 158, 11, 0.12)', borderBottom: '1px solid var(--color-warning)' }}
                 data-testid="pw-change-banner">
              <span>This account is still on a password issued to it. Set your own when convenient.</span>
              <span className="flex gap-2">
                <Button size="sm" onClick={() => setPwOpen(true)} data-testid="pw-change-now">Change now</Button>
                <Button size="sm" variant="outline" onClick={deferPwChange} data-testid="pw-change-later">Later</Button>
              </span>
            </div>
          )}
          <Suspense fallback={<PageLoading />}>
            {accessOk ? (
              // Keyed on the address: an error on one screen does not stick to the next.
              <ErrorBoundary key={location.pathname}><Outlet /></ErrorBoundary>
            ) : <NoAccess label={current?.module?.label} />}
            <ChangePasswordDialog open={pwOpen} prompted={!!user?.must_change_password}
              onClose={() => setPwOpen(false)}
              onDone={async () => { setPwOpen(false); await checkAuth(); }} />
          </Suspense>
        </main>

        {/* Footer */}
        <AppFooter />
      </div>
    </div>
  );
}

export default AppLayout;
